import React from 'react';
import { LineItemsTable } from './components/LineItemsTable';
import { TotalsTable } from './components/TotalsTable';
import { DiscountTable } from './components/DiscountTable';
import PaidAmountTable from './components/PaidAmountTable/PaidAmountTable';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from './InvoiceProductsSection.styles.scss';
import {
  calculateTotalPayableAmount,
  formatNumberWithCurrency,
} from '../../../../../../helpers/invoiceHelpers';
import {
  ZERO_RATING_REASON_CODES,
  EXEMPTION_REASON_CODES,
  OUT_OF_SCOPE_REASON_CODES,
} from '../../../../../../config/invoice/options';

const InvoiceProductsSection = ({
  items,
  documentDiscount,
  roundingAmount,
  prepayment,
  invoice,
}) => {
  const { t, i18n } = useTranslation();

  const discountValues = {
    einvoiceLevelDiscount: documentDiscount?.length > 0,
    invoiceDiscountLineItems: documentDiscount || [],
    roundingAmount: roundingAmount || 0,
    invoicePrepaymentLineItems: prepayment || [],
  };

  const reasonCodesMap = {
    Z: ZERO_RATING_REASON_CODES(i18n.language),
    E: EXEMPTION_REASON_CODES(i18n.language),
    O: OUT_OF_SCOPE_REASON_CODES(i18n.language),
  };

  const exemptionReasons = items.reduce((acc, item) => {
    if (
      ['Z', 'E', 'O'].includes(item?.vatRateType) &&
      item?.taxExemptionReason &&
      !acc[item.vatRateType]
    ) {
      const codes = reasonCodesMap[item.vatRateType];
      const match = codes?.find((c) => c.value === item.taxExemptionReason);
      if (match) {
        acc[item.vatRateType] = match.label;
      }
    }
    return acc;
  }, {});

  const reasonSections = [
    {
      key: 'Z',
      label: 'createInvoice.zero_exemption_reason_code',
      value: exemptionReasons.Z,
    },
    {
      key: 'E',
      label: 'createInvoice.exemption_reason_code',
      value: exemptionReasons.E,
    },
    {
      key: 'O',
      label: 'createInvoice.out_of_scope_reason_code',
      value: exemptionReasons.O,
    },
  ].filter((s) => s.value);

  return (
    <>
      {items[0] && (
        <div>
          <LineItemsTable items={items} invoice={invoice} />
          {documentDiscount?.length > 0 && (
            <div className="mt-4 mb-4">
              <DiscountTable documentDiscount={documentDiscount} />
            </div>
          )}
          <TotalsTable
            items={items}
            invoice={invoice}
            values={discountValues}
          />
          {prepayment[0] && (
            <div className="mt-4 mb-4">
              <PaidAmountTable prepayment={prepayment} />
            </div>
          )}
          {!!Number(roundingAmount) && (
            <div className={cn(styles['rounding-amount'])}>
              <span>{t('createInvoice.rounding_amount')}</span>
              <span>
                {formatNumberWithCurrency(
                  roundingAmount,
                  invoice.currency,
                  i18n.language,
                )}
              </span>
            </div>
          )}
          <div className={styles['totals-container']}>
            <span className={styles['totals-label-container']}>
              <p className={styles['totals-label']}>
                <span className="fw-bold">{t('createInvoice.total')}</span>{' '}
                {t('createInvoice.amount-due-for-payment')}
              </p>
            </span>
            <p className={styles['totals-value']}>
              {formatNumberWithCurrency(
                Number(
                  calculateTotalPayableAmount(items, discountValues),
                ).toFixed(3),
                invoice.currency,
                i18n.language,
              )}
            </p>
          </div>

          <div className={styles['zero-reason-container']}>
            {reasonSections.map((section) => (
              <div key={section.key}>
                <p className={styles['zero-reason-label']}>
                  {t(section.label)}
                </p>
                <p className={styles['zero-reason-value']}>{section.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(InvoiceProductsSection);
